import { useQuery } from "@apollo/client";
import { CircularProgress, Grid } from "@mui/material";
import { debounce } from "lodash";
import { Button, InputItem, Pagination } from "pepsico-ds";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import JsonFormsWrapper from "../../components/jsonForms/jsonFormsWrapper/JsonFormsWrapper";
import { getSegmentsPopulation } from "../../graphql/queries/getSegmentsPopulation";
import { useSegments } from "../../hooks/useSegments";
import {
  segmentationFilterJsonSchema,
  segmentationFilterUISchema,
} from "../../pages/organizations/Segmentation/mocks";
import { paths } from "../../routes/paths";
import ButtonGroupDynamic from "../common/ButtonGroupDynamic";
import "./segmentation.scss";
import SegmentCard from "./SegmentCard/SegmentCard";
import SegmentDialogModals from "./segmentDialogModals/SegmentDialogModals";
import SegmentListTable from "./SegmentListTable/SegmentListTable";

const SegmentsListTab = forwardRef(function SegmentsListTab(
  { type = "", handleCreateClick, showToast },
  ref
) {
  const [viewType, setViewType] = useState("grid");
  const [mainContentY, setMainContentY] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 20; // Default page size
  const [showModal, setShowModal] = useState(false);
  const [action, setAction] = useState(null);
  const [selectedSegment, setSelectedSegment] = useState(null);

  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    status: null,
    population: {
      from: null,
      to: null,
    },
    keyword: null,
    type,
  });

  const { segments, loading, error, refetch, deleteSegmentCall, deleteLoading } =
    useSegments(filters);
  const { data: populationData } = useQuery(getSegmentsPopulation);

  useImperativeHandle(ref, () => ({
    refetch,
  }));

  // Dynamic min, max, and step values for population
  const [dynamicMinMax, setDynamicMinMax] = useState({});

  useEffect(() => {
    if (populationData && populationData.listSegmentsWithPopulationRange) {
      const { minPopulationRange, maxPopulationRange } =
        populationData.listSegmentsWithPopulationRange;

      const calculateStep = (min, max) => {
        const range = max - min;
        if (range === 0) return 1; // Handle zero range to avoid errors
        const exponent = Math.floor(Math.log10(range));
        const step = Math.pow(10, exponent - 1);
        return Math.max(step, 1); // Ensure minimum step size of 1
      };

      const step = calculateStep(minPopulationRange, maxPopulationRange);

      setDynamicMinMax({
        population: {
          min: minPopulationRange,
          max: maxPopulationRange,
          step: step,
        },
      });
    } else {
      // No population data or empty object
      setDynamicMinMax({
        population: {
          min: 0,
          max: 10000000,
          step: 1000000,
        },
      });
    }
  }, [populationData]);

  // Calculate the total number of pages
  const numPages = Math.ceil(segments.length / rowsPerPage);

  // Get the data for the current page
  const currentData = segments.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const handleResize = () => {
    setMainContentY(
      Math.ceil(
        document.getElementsByClassName("main-content")[0].getBoundingClientRect()
          .top
      )
    );
  };

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handlePageChange = (newPageIndex) => {
    if (newPageIndex > 0 && newPageIndex <= numPages) {
      setCurrentPage(newPageIndex);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const debouncedSearchChange = useMemo(() => {
    return debounce((value) => {
      setFilters((prevFilters) => ({
        ...prevFilters,
        keyword: value,
      }));
    }, 1500);
  }, []);
  useEffect(() => {
    return () => {
      debouncedSearchChange.cancel();
    };
  }, [debouncedSearchChange]);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchInput(value);
    debouncedSearchChange(value);
  };

  const handleFilterChange = (newFilters) => {
    setFilters((prevFilters) => {
      const updatedFilters = {
        ...prevFilters,
        ...newFilters,
      };
      if (
        Array.isArray(updatedFilters.status) &&
        updatedFilters.status.length === 1
      ) {
        updatedFilters.status = updatedFilters.status[0];
      } else if (
        Array.isArray(updatedFilters.status) &&
        updatedFilters.status.length === 0
      ) {
        updatedFilters.status = null;
      }
      return updatedFilters;
    });
    setCurrentPage(1);
  };
  const handleClearFilters = () => {
    debouncedSearchChange.cancel();
    setFilters({
      status: null,
      population: {
        from: null,
        to: null,
      },
      keyword: null,
      type,
    });
    setSearchInput("");
    setCurrentPage(1);
  };

  const handleActionClick = (action, segment) => {
    if (action === "delete") {
      setSelectedSegment(segment);
      setShowModal(true);
      setAction("delete_segment");
    }
    if (action === "edit") {
      navigate(
        `/${paths.segmentation}/${paths.segment_list}/${paths.platform_segments}/${paths.edit}/${segment.id}`
      );
    }
    if (action === "view") {
      navigate(`/${paths.segmentation}/${paths.segment_summary_view}/${segment.id}`);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedSegment(null);
    setAction(null);
  };

  const handleModalAction = () => {
    if (action === "delete_segment") {
      handleDeleteSegment();
    }
  };

  const handleDeleteSegment = async () => {
    try {
      const result = await deleteSegmentCall({
        variables: {
          id: selectedSegment.id,
        },
      });
      if (result?.data?.deleteSegment) {
        refetch();
        handleCloseModal();
        showToast("success", "Segment deleted successfully", "");
      } else {
        showToast("error", "Segment delete failed", "");
      }
    } catch (error) {
      showToast("error", "Segment delete failed", error?.message);
    }
  };

  const renderContent = () => {
    // Handle the loading state
    if (loading) {
      return (
        <div className="segment-loading">
          <CircularProgress />
        </div>
      );
    }

    // Handle the case where there are no segments or there's an error
    if (segments.length === 0 || error) {
      return <div className="no-record-found">No Records Found.</div>;
    }

    // Determine the view type (grid or list) and render the appropriate content
    if (viewType === "grid") {
      return (
        <Grid container spacing={2}>
          {currentData.map((segment) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={segment.id}>
              <SegmentCard
                segment={segment}
                segmentType={type}
                handleActionClick={(action) => handleActionClick(action, segment)}
              />
            </Grid>
          ))}
        </Grid>
      );
    } else {
      return (
        <SegmentListTable
          segmentData={currentData}
          segmentType={type}
          handleActionClick={handleActionClick}
        />
      );
    }
  };

  return (
    <div className="page-content segment-list-tab">
      <div className="view-filter">
        <div className="segment-filters-container">
          <InputItem
            placeholder="Search name or description"
            trailingIcon="search"
            size="medium"
            className="input-search"
            onChange={handleSearchChange}
            value={searchInput}
          />

          <JsonFormsWrapper
            jsonschema={segmentationFilterJsonSchema}
            uischema={segmentationFilterUISchema}
            data={filters}
            onChange={handleFilterChange}
            config={{ dynamicMinMax }}
          />

          {/* <RangeSliderControl
            data={{ from: filters.populationFrom, to: filters.populationTo }}
            handleChange={handlePopulationChange}
          /> */}
          <p className="clear-all-button" onClick={handleClearFilters}>
            Clear Filter
          </p>
        </div>
        <ButtonGroupDynamic
          items={[
            {
              icon: "view_module",
              onClick: () => {
                setViewType("grid");
              },
            },
            {
              icon: "view_list",
              onClick: () => {
                setViewType("list");
              },
            },
          ]}
          variant="toggle"
          selectedIndex={viewType === "grid" ? 0 : 1}
        />
        {type === "PLATFORM" && (
          <Button
            variant="primary"
            size="medium"
            className="ml-3"
            onClick={handleCreateClick}
          >
            Create Segment
          </Button>
        )}
      </div>
      <div
        className="main-content"
        style={{
          flex: 20,
          overflowY: "auto",
          maxHeight: `calc(100vh - ${mainContentY}px)`,
        }}
      >
        {renderContent()}
        <div
          style={{
            padding: "16px 0",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Pagination
            currentPage={currentPage}
            onUpdate={handlePageChange}
            pageCount={numPages}
            size="medium"
            variant="number"
          />
        </div>
      </div>
      <SegmentDialogModals
        open={showModal}
        handleClose={handleCloseModal}
        action={action}
        segmentData={selectedSegment}
        handleModalAction={handleModalAction}
        loading={deleteLoading}
      />
    </div>
  );
});

SegmentsListTab.displayName = "SegmentsListTab";

export default SegmentsListTab;
